// api/fetchClient.ts

import { getSearchParams, getWindow } from '../utils/window.utils';
import { HttpError } from './http-error';

interface FetchOptions extends Omit<RequestInit, 'headers'> {
  body?: any;
  params?: Record<string, any>; // Add params support
  headers?: Record<string, string | null | undefined> | Headers | [string, string][];
}

class FetchClient {
  private readonly baseURL: string;
  private readonly localePrefix: string;

  constructor(baseURL: string = window.location.origin) {
    this.baseURL = baseURL;
    this.localePrefix = this.detectLocalePrefix();
  }

  /**
   * Shopify multi-language stores prefix paths with a locale code.
   * e.g., /fr/apps/single/... or /en-ca/apps/single/...
   * We need to prepend this to all API calls so the app proxy resolves correctly.
   */
  private detectLocalePrefix(): string {
    const path = window.location.pathname;
    const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\/apps\/single/i);
    if (match) {
      return '/' + match[1];
    }
    return '';
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    const userId = localStorage.getItem('single_customer_id');
    const email = localStorage.getItem('single_customer_email');

    if (userId) headers['x-single-shopify-user-id'] = userId;
    if (email) headers['x-single-shopify-user-email'] = email;

    return headers;
  }

  private mergeHeaders(
    finalHeaders: Record<string, string>,
    headers?: Record<string, string | null | undefined> | Headers | [string, string][]
  ): void {
    // Always add auth headers
    Object.assign(finalHeaders, this.getAuthHeaders());

    // Merge provided headers (these override defaults)
    if (headers) {
      if (headers instanceof Headers) {
        headers.forEach((value, key) => {
          finalHeaders[key] = value;
        });
      } else if (Array.isArray(headers)) {
        headers.forEach(([key, value]) => {
          finalHeaders[key] = value;
        });
      } else {
        Object.assign(finalHeaders, headers);
      }
    }
  }

  // Add the default params method
  private addDefaultParams(params: Record<string, any> = {}): Record<string, any> {
    const windowParams = getSearchParams();
    const themeId = getWindow()?.Shopify?.theme?.id;
    const accessKey = windowParams?.get('access-key');

    if (themeId) {
      params.themeId = themeId;
    }

    if (accessKey && accessKey.length > 0) {
      params.accessKey = accessKey;
    }

    return params;
  }

  // Helper to build URL with query params
  private buildUrl(endpoint: string, params?: Record<string, any>): string {
    // Prepend locale prefix to the endpoint if it starts with /apps/single
    let prefixedEndpoint = endpoint;
    if (this.localePrefix && endpoint.startsWith('/apps/single')) {
      prefixedEndpoint = this.localePrefix + endpoint;
    }

    const url = new URL(prefixedEndpoint, this.baseURL);

    const enrichedParams = this.addDefaultParams(params);
    let sortParam: string | undefined;

    Object.entries(enrichedParams).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === 'sort') {
        sortParam = value;
      } else {
        url.searchParams.append(key, String(value));
      }
    });

    let finalUrl = url.toString();
    if (sortParam) {
      const prefix = finalUrl.includes('?') ? '&' : '?';
      finalUrl += `${prefix}sort=${sortParam}`;
    }

    return finalUrl;
  }

  async fetch<T = any>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const { headers, body, params, ...restOptions } = options;

    const finalHeaders: Record<string, string> = {
      Accept: '*/*',
      'Content-Type': 'application/json',
    };

    // Override Content-Type for FormData/Blob (let browser set it)
    const isFormDataOrBlob = body instanceof FormData || body instanceof Blob;
    if (isFormDataOrBlob) {
      delete finalHeaders['Content-Type'];
    }

    this.mergeHeaders(finalHeaders, headers);

    // Auto-stringify JSON bodies (but not FormData/Blob)
    let processedBody: BodyInit | undefined = body as BodyInit | undefined;
    if (body && typeof body === 'object' && !isFormDataOrBlob) {
      processedBody = JSON.stringify(body);
    }

    // Build URL with params (automatically adds default params)
    // For FormData/Blob requests, still use buildUrl for locale prefix but skip params
    const url = this.buildUrl(endpoint, isFormDataOrBlob ? undefined : params);

    const response = await fetch(url, {
      ...restOptions,
      body: processedBody,
      headers: finalHeaders,
    });

    if (!response.ok) {
      const body = await response.text().catch(() => undefined);
      throw new HttpError(response.status, body);
    }

    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');

    // Return null for empty responses (204 No Content or empty body)
    if (response.status === 204 || contentLength === '0') {
      return null as unknown as T;
    }

    // Only parse JSON if content-type indicates JSON or if there's content
    if (contentType?.includes('application/json')) {
      const text = await response.text();
      return (text ? JSON.parse(text) : null) as T;
    }

    // For non-JSON responses or when unsure, try to parse as JSON but handle empty responses
    const text = await response.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  // Convenience methods
  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'GET', params });
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'POST', body, params });
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'PUT', body, params });
  }

  async delete<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'DELETE', params });
  }

  async getText(
    endpoint: string,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<any> {
    return await this.fetchResponse(endpoint, params, { ...options, method: 'GET' }).then(
      (response) => response.text()
    );
  }

  async postText(
    endpoint: string,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<any> {
    return await this.fetchResponse(endpoint, params, { ...options, method: 'POST' }).then(
      (response) => response.text()
    );
  }

  async getStatus(
    endpoint: string,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<number> {
    return await this.fetchResponse(endpoint, params, { ...options, method: 'GET' }).then(
      (response) => response.status
    );
  }

  async fetchResponse(
    endpoint: string,
    params?: Record<string, any>,
    options: FetchOptions = {}
  ): Promise<Response> {
    const { headers, ...restOptions } = options;
    const finalHeaders: Record<string, string> = {
      Accept: '*/*',
    };
    this.mergeHeaders(finalHeaders, headers);

    const url = this.buildUrl(endpoint, params);
    const response = await fetch(url, {
      ...restOptions,
      headers: finalHeaders,
    });

    if (!response.ok) {
      throw new HttpError(response.status);
    }
    return response;
  }
}

export const apiClient = new FetchClient(window.location.origin);
